import React, { useState } from "react";
import { useLang } from "../context/LangContext";
import { useSettings } from "../context/SettingsContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { useToast } from "../components/Toast";
import { Phone, Mail, MapPin } from "lucide-react";

export default function ContactPage() {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const { push: toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return;
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast(t("contact_success"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail) || "Error", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pt-28 md:pt-32 pb-20 px-5 md:px-10 max-w-7xl mx-auto" data-testid="contact-page">
      <div className="mb-12">
        <span className="text-xs tracking-[0.3em] uppercase opacity-60 block mb-3">{t("nav_contact")}</span>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">{t("contact_title")}</h1>
        <p className="mt-4 max-w-xl opacity-80">{t("contact_sub")}</p>
      </div>

      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5 space-y-6">
          <div className="card p-6 space-y-3">
            {settings.contact_phone && (
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--secondary)" }}>
                  <Phone size={16} />
                </span>
                <div>
                  <div className="text-xs opacity-60">{t("phone")}</div>
                  <div>{settings.contact_phone}</div>
                </div>
              </div>
            )}
            {settings.contact_email && (
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--secondary)" }}>
                  <Mail size={16} />
                </span>
                <div>
                  <div className="text-xs opacity-60">{t("email")}</div>
                  <div>{settings.contact_email}</div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--secondary)" }}>
                <MapPin size={16} />
              </span>
              <div>
                <div className="text-xs opacity-60">{t("location")}</div>
                <div>{lang === "mn" ? settings.contact_address_mn : settings.contact_address_en}</div>
              </div>
            </div>
          </div>

          {settings.map_embed_url && (
            <div className="overflow-hidden rounded-2xl border border-[color:var(--border)]" data-testid="contact-map">
              <iframe
                title="map"
                src={settings.map_embed_url}
                width="100%"
                height="280"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
        </div>

        <form onSubmit={submit} className="md:col-span-7 card p-6 md:p-10 space-y-6" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs tracking-widest uppercase opacity-60 mb-1">{t("your_name")}</label>
              <input
                className="input-line"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-name"
                required
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase opacity-60 mb-1">{t("your_phone")}</label>
              <input
                className="input-line"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="contact-phone"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase opacity-60 mb-1">{t("email")}</label>
            <input
              className="input-line"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="contact-email"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase opacity-60 mb-1">{t("message")}</label>
            <textarea
              className="input-box"
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="contact-message"
              required
            />
          </div>
          <button className="btn btn-primary" disabled={busy} data-testid="contact-submit">
            {t("send_msg")}
          </button>
        </form>
      </div>
    </section>
  );
}
